import { Box, IconButton, Typography } from '@mui/material'
import { useState, useMemo } from 'react'
import React from 'react'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  DROPDOWN_DEFAULT_VALUE,
  FIELD_TYPES,
  FORMATS,
  STANDARD_COLORS,
} from '../../../utility/constants'
import { fileServices } from '../../../services/fileServices'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { getFieldId, getKeyOfValue } from '../../../pages/DataEntryPage/utils/utils'
import LongMenu from '../../../pages/DataEntryPage/Fields/LongMenu'
import StandaloneFilePreview from '../../Dashboard/PreviewComponent/components/StandaloneFilePreview'
import { extractDisplayName } from '../../../utility/fileUtil'
import FieldHistory from '../../DataEntry/FieldHistory'
import { formatCurrency } from '../../../pages/DataEntryPage/utils/currency'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'

import { getDateAndDateTime } from '../util'
import moment from 'moment'
import { formatUserFullName } from '../../../utility/userName'
import { replacePlaceholders } from '../../../pages/DataEntryPage/utils/placeholderUtil'

const NormalFieldPreview = ({
  activityType,
  field,
  value,
  review,
  index,
  previewCurrency,
  listData,
  dynamicListData,
  processId,
  data,
  isPreview = false,
  defaultCurrency = {},
  externalValues = {},
  fieldMappings = {},
  fieldMetadata = {},
}) => {
  const { user } = useAuthContext()
  const [filePreview, setFilePreview] = useState(null)

  // Build O(1) lookup maps once from the listData array
  const listDataIdMap = useMemo(
    () =>
      (listData || []).reduce((acc, it) => {
        acc[it.id] = it
        return acc
      }, {}),
    [listData],
  )

  const listIdsWithPlaceholders = useMemo(() => {
    const result = new Set()
    const seen = new Set()
    for (const it of listData || []) {
      if (it.list_id == null || seen.has(it.list_id)) continue
      seen.add(it.list_id)
      if (Array.isArray(it.placeholders) && it.placeholders.length > 0) {
        result.add(it.list_id)
      }
    }
    return result
  }, [listData])

  const downloadFile = async (value) => {
    await fileServices.downloadFile(user, value)
  }

  const renderValue = (field) => {
    if (field.data_structure === FIELD_TYPES.CURRENCY) {
      return formatCurrency(
        value,
        getKeyOfValue(CURRENCY_COUNTRY_CODE, previewCurrency[field.id]),
        {
          defaultCurrency: defaultCurrency,
        },
      )
    } else if (
      field.data_structure === FIELD_TYPES.DATE ||
      field.data_structure === FIELD_TYPES.DATETIME
    ) {
      return getDateAndDateTime(value, field.data_structure)
    } else if (field.data_structure === FIELD_TYPES.FILE && value) {
      return extractDisplayName(value)
    } else if (
      [
        FIELD_TYPES.TIMESTAMPED_BIGTEXT,
        FIELD_TYPES.TIMESTAMPED_DATE,
        FIELD_TYPES.TIMESTAMPED_DATETIME,
      ].includes(field.data_structure)
    ) {
      if (!value || !value.length) return ''

      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {value.map((v, index) => {
            const user = dynamicListData?.users?.find((u) => u.id === v.user_id)
            const username = user ? formatUserFullName(user) : null
            const text = typeof v.text === 'string' ? v.text.trimStart() : v.text

            return (
              <div key={index} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <span style={{ fontWeight: 600 }}>{`${moment(v?.timestamp).format(
                  FORMATS.DATETIME,
                )}:`}</span>{' '}
                {text}
                <span style={{ fontStyle: 'italic', color: '#888' }}>{` - ${username}`}</span>
              </div>
            )
          })}
        </div>
      )
    } else if (
      field.data_structure === FIELD_TYPES.LIST ||
      field.data_structure === FIELD_TYPES.LIST_MULTI
    ) {
      const valuesArray = Array.isArray(value) ? value : [value]
      const resolvedLabels = valuesArray.map((val) => {
        if (val == null || val === '' || val == DROPDOWN_DEFAULT_VALUE) {
          return ''
        }
        if (!field.list_id) {
          const type = field?.dynamic_field_type?.type
          const items = dynamicListData?.[type]
          const item = items?.find((ld) => ld.id == val)
          return type == 'users' ? (item ? formatUserFullName(item) : val) : item ? item?.name : val
        } else {
          const item = Array.isArray(listData) ? listData.find((ld) => ld.id == val) : null
          const listDataValue = item ? item.value : val
          return replacePlaceholders(
            listDataValue,
            { ...data.data, ...externalValues },
            listDataIdMap,
            fieldMappings[field.id] || field.metadata?.placeholder_map || {},
            '',
            fieldMetadata,
          )
        }
      })
      return resolvedLabels.filter(Boolean).join(', ')
    } else if (typeof value === 'string' && value.includes('[[')) {
      return replacePlaceholders(
        value,
        { ...data.data, ...externalValues },
        listDataIdMap,
        fieldMappings[field.id] || field.metadata?.placeholder_map || {},
        '',
        fieldMetadata,
      )
    } else if (field.data_structure == FIELD_TYPES.BIGTEXT) {
      return <span style={{ position: 'relative' }}>{value}</span>
    } else {
      return <span style={{ position: 'relative' }}>{value}</span>
    }
  }

  return (
    <div className="preview-field-class" style={{ marginBottom: 8 }}>
      <Box
        id={getFieldId(field.id)}
        style={{
          display: 'flex',
          flexDirection: 'row',
          fontSize: 14,
          padding: '4px 8px',
          backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'transparent',
          borderRadius: 4,
          border: '1px solid #e1d9fb',
          width: '100%',
          gap: 8,
          alignItems: 'center',
          overflowX: 'hidden',
        }}
      >
        <div style={{ width: '40%', marginBottom: 0, minWidth: 0 }}>
          <Typography style={{ fontSize: '14px' }}>
            {field.field_template_name}
            {field.is_required ? '*' : ''}
          </Typography>
        </div>

        <div
          style={{
            width: '60%',
            marginBottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: 0,
            overflowX: 'hidden',
          }}
        >
          <FieldHistory
            className="previewfieldhostory"
            fieldId={field.id}
            field={field}
            listDataIdMap={listDataIdMap}
            listIdsWithPlaceholders={listIdsWithPlaceholders}
            dynamicListData={dynamicListData}
            processId={processId}
            allValues={data.data}
            isPreview={isPreview}
            width="auto"
          >
            <Typography
              style={{
                fontSize: 14,
                whiteSpace: 'pre-line',
                fontWeight: '500',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',

                color:
                  activityType === ACTIVITY_TYPES.REVIEW
                    ? review === undefined
                      ? null
                      : review
                        ? STANDARD_COLORS.PREVIEW_ACCEPTED
                        : STANDARD_COLORS.PREVIEW_REJECTED
                    : activityType === ACTIVITY_TYPES.LONG_RUNNING
                      ? review === true
                        ? STANDARD_COLORS.PREVIEW_ACCEPTED
                        : STANDARD_COLORS.PREVIEW_REJECTED
                      : null,
              }}
            >
              {renderValue(field)}
            </Typography>
          </FieldHistory>
          {field.data_structure === FIELD_TYPES.FILE && value && (
            <LongMenu
              options={[
                {
                  label: 'Preview',
                  icon: (
                    <IconButton size="small" sx={{ padding: '0px 4px' }}>
                      <RemoveRedEyeOutlinedIcon fontSize="18px" />
                    </IconButton>
                  ),
                  onClick: () => setFilePreview(value),
                },
                {
                  label: 'Download',
                  icon: (
                    <IconButton size="small" sx={{ padding: '0px 4px' }}>
                      <FileDownloadOutlinedIcon fontSize="18px" />
                    </IconButton>
                  ),
                  onClick: () => downloadFile(value),
                },
              ]}
            />
          )}
        </div>
      </Box>
      <StandaloneFilePreview
        fileKey={filePreview}
        open={filePreview != null}
        onClose={() => setFilePreview(null)}
      />
    </div>
  )
}

export default NormalFieldPreview
